'use client';

import { ArrowRight } from 'lucide-react';

import Link from 'next/link';

import { APP_ENVS } from '@/shared/constants/envs';
import { APP_ROUTES } from '@/shared/constants/navigation';

import { ALTERNATIVES_PAGES } from '../../constants/alternatives-pages';
import {
	getAlternativesHref,
	getAlternativesShortTitle,
	getAnchorProfile,
} from '../../constants/alternatives-shared';
import { COMPARISON_PAGES } from '../../constants/comparison-pages';
import { INLINE_LINK_CLASS } from '../../constants/link-class';
import {
	BINARY_HUB_ROW,
	COMPARISON_HUB_CATEGORIES,
	type ComparisonData,
	type ComparisonHubCategory,
} from '../../constants/comparison-shared';
import { MARKETING_PAGE } from '../../utils/marketing-analytics';
import { BinaryLogo, CompetitorLogo } from '../common/competitor-logo';
import { RoundupLogo } from '../common/roundup-logo';
import { FooterSection } from '../views/footer-section';
import { HeaderSection } from '../views/header-section';

/** What each hub category covers, shown under its H2. */
const CATEGORY_INTROS: Record<ComparisonHubCategory, string> = {
	'Enterprise ATS':
		'Sales-led platforms priced on headcount with annual contracts. Deep configuration, but no free plan and setup measured in weeks.',
	'Startup & SMB ATS':
		'Self-serve tools for small teams. Most have a free plan or trial; the differences are in job caps, AI and what counts as an add-on.',
	'HR suites with hiring':
		'HRIS and payroll platforms where recruiting is one module. Good if you want one system of record, with a lighter ATS and per-employee pricing.',
	'Agency & staffing CRMs':
		'ATS plus client and deal management for recruitment firms, priced per user. Built for placements rather than in-house hiring.',
};

const compareHref = (page: ComparisonData) => `/compare/${page.slug}`;

function ComparePage() {
	const companyName = APP_ENVS.COMPANY_NAME;

	return (
		<div className="flex min-h-screen w-full flex-col bg-[#F8F8F8]">
			<HeaderSection page={MARKETING_PAGE.COMPARISON} />

			<div className="flex w-full flex-1 flex-col items-center justify-start pt-8 md:pt-16">
				<div className="landing-width mb-12 flex w-full flex-col items-center gap-4 px-4 text-center md:mb-16 md:px-8">
					<h1 className="landing-heading mx-auto max-w-3xl px-1 !text-[28px] !tracking-tight sm:!text-[36px] md:!text-[46px]">
						Compare {companyName} with other ATS platforms
					</h1>
					<p className="mx-auto max-w-2xl text-sm text-slate-700 md:text-base">
						{companyName} is an AI-first ATS that works inside Claude and ChatGPT through{' '}
						<Link
							href={APP_ROUTES.MCP}
							className={INLINE_LINK_CLASS}
						>
							MCP
						</Link>
						, with a{' '}
						<Link
							href={APP_ROUTES.FREE_ATS}
							className={INLINE_LINK_CLASS}
						>
							free-forever plan
						</Link>
						. See how it stacks up against {COMPARISON_PAGES.length} other applicant tracking
						systems on price, free plans, AI agents and MCP. Looking for no-cost options? Read our
						roundup of the{' '}
						<Link
							href={APP_ROUTES.BEST_FREE_ATS}
							className={INLINE_LINK_CLASS}
						>
							best free ATS
						</Link>{' '}
						for 2026.
					</p>
				</div>

				<div className="landing-width mb-20 w-full px-4 md:mb-28 md:px-8">
					<div className="mx-auto flex max-w-4xl flex-col gap-14">
						{COMPARISON_HUB_CATEGORIES.map((category) => {
							const pages = COMPARISON_PAGES.filter((page) => page.hub.category === category);
							if (pages.length === 0) return null;

							return (
								<section key={category}>
									<h2 className="landing-heading mb-2 text-left !text-[22px] md:!text-[28px]">
										{category}
									</h2>
									<p className="mb-6 text-sm text-slate-700 md:text-base">
										{CATEGORY_INTROS[category]}
									</p>
									<ul className="flex flex-col gap-3">
										{pages.map((page) => (
											<li key={page.slug}>
												<Link
													href={compareHref(page)}
													className="group flex items-start justify-between gap-4 rounded-md border border-border bg-background px-4 py-3.5 transition-all hover:-translate-y-0.5 hover:shadow-md md:px-5 md:py-4"
												>
													<CompetitorLogo
														slug={page.slug}
														name={page.competitor}
														size="md"
														className="mt-0.5"
													/>
													<div className="flex flex-1 flex-col gap-1">
														<h3 className="text-sm font-semibold text-primary md:text-base">
															{companyName} vs {page.competitor}
														</h3>
														<p className="text-sm leading-relaxed text-slate-700">
															{page.hub.verdict}
														</p>
													</div>
													<ArrowRight className="mt-1 h-4 w-4 shrink-0 text-primary transition-transform group-hover:translate-x-0.5" />
												</Link>
											</li>
										))}
									</ul>
								</section>
							);
						})}
					</div>
				</div>

				<div className="landing-width mb-20 w-full px-4 md:mb-28 md:px-8">
					<div className="mx-auto max-w-4xl">
						<h2 className="landing-heading mb-2 text-left !text-[22px] md:!text-[28px]">
							Leaving a specific ATS? Start with its alternatives guide
						</h2>
						<p className="mb-6 text-sm text-slate-700 md:text-base">
							Each guide ranks the best alternatives to one tool, with a table of price, AI support
							and simplicity, the reasons teams leave it and who each option is for.
						</p>
						<ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
							{ALTERNATIVES_PAGES.map((page) => {
								const profile = getAnchorProfile(page);

								return (
									<li key={page.slug}>
										<Link
											href={getAlternativesHref(page)}
											className="group flex h-full items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-3 text-sm font-medium text-primary transition-all hover:-translate-y-0.5 hover:shadow-md"
										>
											<span className="flex min-w-0 items-center gap-2.5">
												<RoundupLogo
													logo={profile.logo}
													name={profile.name}
													size="sm"
												/>
												<span className="min-w-0 leading-snug">{getAlternativesShortTitle(page)}</span>
											</span>
											<ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
										</Link>
									</li>
								);
							})}
						</ul>
					</div>
				</div>

				<div className="landing-width mb-24 w-full px-4 md:mb-40 md:px-8">
					<div className="mx-auto max-w-5xl">
						<h2 className="landing-heading mb-2 text-left !text-[22px] md:!text-[28px]">
							Every ATS at a glance
						</h2>
						<p className="mb-6 text-sm text-slate-700 md:text-base">
							Price, free plan, AI agent and MCP support for each platform, from vendor pricing
							pages checked October 2026. Binary is the only one with an AI agent and an MCP
							server on a free plan.
						</p>
						<div className="overflow-x-auto rounded-xl border border-border bg-background">
							<table className="w-full min-w-[720px] border-collapse text-left text-sm">
								<thead>
									<tr className="border-b border-border bg-accent/50">
										<th className="px-4 py-3 font-semibold text-primary">ATS</th>
										<th className="px-4 py-3 font-semibold text-primary">Pricing</th>
										<th className="px-4 py-3 font-semibold text-primary">Free plan</th>
										<th className="px-4 py-3 font-semibold text-primary">AI agent</th>
										<th className="px-4 py-3 font-semibold text-primary">MCP server</th>
									</tr>
								</thead>
								<tbody>
									<tr className="border-b border-border bg-emerald-50/60">
										<td className="px-4 py-3 font-semibold text-primary">
											<span className="flex items-center gap-2.5">
												<BinaryLogo size="sm" />
												{BINARY_HUB_ROW.name}
											</span>
										</td>
										<td className="px-4 py-3 text-slate-700">{BINARY_HUB_ROW.pricing}</td>
										<td className="px-4 py-3 text-slate-700">{BINARY_HUB_ROW.freePlan}</td>
										<td className="px-4 py-3 text-slate-700">{BINARY_HUB_ROW.aiAgent}</td>
										<td className="px-4 py-3 text-slate-700">{BINARY_HUB_ROW.mcp}</td>
									</tr>
									{COMPARISON_PAGES.map((page) => (
										<tr
											key={page.slug}
											className="border-b border-border last:border-b-0"
										>
											<td className="whitespace-nowrap px-4 py-3 font-semibold text-primary">
												<Link
													href={compareHref(page)}
													className="flex items-center gap-2.5 underline decoration-1 decoration-slate-300 underline-offset-4 hover:decoration-primary"
												>
													<CompetitorLogo
														slug={page.slug}
														name={page.competitor}
														size="sm"
													/>
													{page.competitor}
												</Link>
											</td>
											<td className="px-4 py-3 text-slate-700">{page.hub.pricing}</td>
											<td className="px-4 py-3 text-slate-700">{page.hub.freePlan}</td>
											<td className="px-4 py-3 text-slate-700">{page.hub.aiAgent}</td>
											<td className="px-4 py-3 text-slate-700">{page.hub.mcp}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>

			<FooterSection />
		</div>
	);
}

export default ComparePage;
